import { useCallback, useEffect } from "react";

import type { EntityViewRow } from "#app/lib/data";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { findViewByKey } from "#app/lib/views/view-routes";

/**
 * Where a scope's bare URL lands (`/workflow/<id>`, `/team/<id>`).
 *
 * The strip's tabs are shared, and one of them may carry the scope's
 * `isDefault`. That flag is where a person lands the FIRST time they open the
 * scope — not where they land every time. Leaving for Contacts and coming
 * back reopens the view they were on, the way the browser's back button
 * would: a bare link forgets nothing.
 *
 * The memory is the viewer's, one key per scope in the user settings bag
 * (`last-view:<kind>:<id>`), next to the tab order and pins that already
 * live there — so it follows the person across devices. An explicit view URL
 * is never rewritten, and landing on one by any route (a tab click, a shared
 * link, a bookmark) records it: that IS the last view the person saw here.
 *
 * Resolution order for a bare URL:
 *   1. the remembered view, if the row still holds it and it is reachable
 *   2. the scope's default view
 *   3. the first view in the row
 * A remembered view that was deleted, or whose surface sits behind a flag
 * that is off, falls through without a trace.
 */

/** Any tabbed scope — a saved-views scope or the team page's fixed strip. */
type LastViewScope = { kind: string; id: string };

export function lastViewSettingKey(scope: LastViewScope): string {
  return `last-view:${scope.kind}:${scope.id}`;
}

// Module-level parser — useUserSetting derives its value off parse identity.
const parseLastView = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

/**
 * The viewer's remembered view key for `scope` — a route segment: a built-in
 * key (`list`), a saved view's id, or a team tab. `initialLoading` is true
 * until the first read lands; a redirect that decides before then sends
 * everyone to the default, which is the one thing the memory exists to
 * avoid.
 */
export function useLastView(scope: LastViewScope): {
  lastKey: string | null;
  remember: (key: string) => void;
  initialLoading: boolean;
} {
  const { value, setValue, initialLoading } = useUserSetting(
    lastViewSettingKey(scope),
    parseLastView,
  );
  const remember = useCallback(
    (key: string) => {
      if (key !== value) setValue(key);
    },
    [value, setValue],
  );
  return { lastKey: value, remember, initialLoading };
}

/**
 * Records `key` as the scope's last view whenever it changes. Pass `null`
 * while the route names no view (the scope's own index, a view the row no
 * longer holds) — nothing is written then. Writes wait for the first read:
 * a write against an unloaded value would land before the read and be
 * undone by it, then re-issued, for two round trips that say the same thing.
 */
export function useRememberLastView(
  scope: LastViewScope,
  key: string | null,
): void {
  const { lastKey, remember, initialLoading } = useLastView(scope);
  useEffect(() => {
    if (initialLoading || !key || key === lastKey) return;
    remember(key);
  }, [initialLoading, key, lastKey, remember]);
}

/**
 * The saved view a scope's bare URL opens: the remembered one, else the
 * default, else the first. `views` is the REACHABLE row — the caller has
 * already dropped any view whose surface a flag hides — so a stale memory of
 * a hidden or deleted view falls through here without special-casing.
 */
export function resolveLandingView(
  views: EntityViewRow[],
  lastKey: string | null,
): EntityViewRow | null {
  return (
    findViewByKey(views, lastKey) ??
    views.find((view) => view.isDefault) ??
    views[0] ??
    null
  );
}

/**
 * The fixed-strip counterpart (the team page): the remembered tab when it is
 * one the strip currently shows, else `fallback`.
 */
export function resolveLandingTab<T extends string>(
  available: readonly T[],
  lastKey: string | null,
  fallback: T,
): T {
  const match = available.find((tab) => tab === lastKey);
  return match ?? fallback;
}
